import { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import GalleryPage from '../pages/GalleryPage';
import { API_BASE_URL } from '../config/api';

const BARE_ROOT = '/';

// Serves the "/" route. When config.json sets "defaultPage" to something other
// than the bare root (e.g. "/?tag=favorite" or "/note/home"), visitors are
// redirected there. A bare root keeps the classic full gallery.
function DefaultPageRedirect() {
  const [defaultPage, setDefaultPage] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let cancelled = false;
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/config`);
        if (!cancelled) {
          const configured = response.data?.data?.defaultPage;
          setDefaultPage(
            configured && configured.trim() ? configured.trim() : BARE_ROOT
          );
        }
      } catch (err) {
        // On failure fall back to the full gallery rather than blocking the page.
        console.error('Error loading defaultPage config:', err);
        if (!cancelled) {
          setDefaultPage(BARE_ROOT);
        }
      }
    };
    fetchConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  if (defaultPage === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  // A bare root means the gallery itself is the home page (all designs).
  if (defaultPage === BARE_ROOT || defaultPage === '') {
    return <GalleryPage />;
  }

  // If defaultPage targets the gallery root with a query (e.g. "/?tag=favorite"),
  // render the gallery once the URL already carries a tag/category filter. This
  // prevents a redirect loop and lets explicit links like "/?tag=all" win.
  const targetsGalleryRoot = defaultPage.startsWith('/?');
  const hasTagParam =
    searchParams.has('tag') || searchParams.has('category');

  if (targetsGalleryRoot && hasTagParam) {
    return <GalleryPage />;
  }

  return <Navigate to={defaultPage} replace />;
}

export default DefaultPageRedirect;
